import React, {useMemo} from 'react'
import styled from 'styled-components'
import {useTranslation} from 'i18n'
import {getFontFamily, getFontSize} from 'utils'
import {Paragraph, ParsedText} from 'common/components'
import convertUnit from 'lib/unit'
import {parseShape} from 'types/components'
import {LegalSellGuidelinesProps} from './LegalSellGuidelinesProps'

interface ParagraphProps {
  isCreatorPortal?: boolean
}
const StyledListItem = styled.li<ParagraphProps>`
  ${({isCreatorPortal}) => ({
    paddingLeft: convertUnit(isCreatorPortal ? 25 : 16),
    fontSize: getFontSize(isCreatorPortal ? 'l' : 's'),
    lineHeight: convertUnit(isCreatorPortal ? 31 : 26),
  })}
  font-family: ${getFontFamily('regular')};
`
const StyledOL = styled.ol<ParagraphProps>`
  padding: 0;
  margin: 0;
  padding-left: ${({isCreatorPortal}) =>
    convertUnit(isCreatorPortal ? 25 : 16)};
  font-family: ${getFontFamily('regular')};
  font-size: ${({isCreatorPortal}) => getFontSize(isCreatorPortal ? 'l' : 's')};
`

const StyledInfoContainer = styled.div<ParagraphProps>`
  display: flex;
  flex-direction: column;
  ${({isCreatorPortal}) => ({
    padding: convertUnit(isCreatorPortal ? 0 : 16),
    paddingTop: convertUnit(isCreatorPortal ? 12 : 4),
  })}
`

const StyledTitle = styled(Paragraph)<ParagraphProps>`
  padding: 0 ${({isCreatorPortal}) => (isCreatorPortal ? 0 : convertUnit(16))};
  margin-top: ${({isCreatorPortal}) => (isCreatorPortal ? convertUnit(20) : 0)};
  line-height: ${convertUnit(26)};
`

const StyledParagraph = styled(Paragraph)<ParagraphProps>`
  ${({isCreatorPortal}) => ({
    lineHeight: isCreatorPortal ? convertUnit(31) : convertUnit(21),
    fontSize: getFontSize(isCreatorPortal ? 'l' : 's'),
  })}
`

export default function LegalSellGuidelines({
  isVerified,
  isCreatorPortal,
}: LegalSellGuidelinesProps) {
  const {translate} = useTranslation()

  const patterns: parseShape[] = useMemo(() => {
    const regex = /\*\*(.+?)\*\*/
    return [
      {
        pattern: regex,
        style: {fontFamily: getFontFamily('bold')},
        renderText: (text) => text.replace(regex, `$1`),
      },
    ]
  }, [])
  const handleRenderTitle = useMemo(
    () => (
      <StyledTitle
        fontSize={isCreatorPortal ? 'xl' : 'm'}
        fontWeight="bold"
        isCreatorPortal={isCreatorPortal}>
        {translate('policy:guidelineSellTitle')}
      </StyledTitle>
    ),
    [isCreatorPortal, translate],
  )

  const handleRenderInfo = useMemo(
    () => (
      <StyledInfoContainer isCreatorPortal={isCreatorPortal}>
        <StyledOL isCreatorPortal={isCreatorPortal}>
          <StyledListItem isCreatorPortal={isCreatorPortal}>
            <StyledParagraph isCreatorPortal={isCreatorPortal}>
              {translate('policy:guidelineSellContent1')}
            </StyledParagraph>
          </StyledListItem>
          <StyledListItem isCreatorPortal={isCreatorPortal}>
            <StyledParagraph isCreatorPortal={isCreatorPortal}>
              {translate('policy:guidelineSellContent2')}
            </StyledParagraph>
          </StyledListItem>
          <StyledListItem isCreatorPortal={isCreatorPortal}>
            <StyledParagraph isCreatorPortal={isCreatorPortal}>
              {translate('policy:guidelineSellContent3')}
            </StyledParagraph>
          </StyledListItem>
          <StyledListItem isCreatorPortal={isCreatorPortal}>
            <StyledParagraph isCreatorPortal={isCreatorPortal}>
              <ParsedText parse={patterns}>
                {translate('policy:guidelineSellContent4')}
              </ParsedText>
            </StyledParagraph>
          </StyledListItem>
          <StyledListItem isCreatorPortal={isCreatorPortal}>
            <StyledParagraph isCreatorPortal={isCreatorPortal}>
              {translate('policy:guidelineSellContent5')}
            </StyledParagraph>
          </StyledListItem>
          <StyledListItem isCreatorPortal={isCreatorPortal}>
            <StyledParagraph isCreatorPortal={isCreatorPortal}>
              {translate('policy:guidelineSellContent6')}
            </StyledParagraph>
          </StyledListItem>
          <StyledListItem isCreatorPortal={isCreatorPortal}>
            <StyledParagraph isCreatorPortal={isCreatorPortal}>
              {translate('policy:guidelineSellContent7')}
            </StyledParagraph>
          </StyledListItem>
          <StyledListItem isCreatorPortal={isCreatorPortal}>
            <StyledParagraph isCreatorPortal={isCreatorPortal}>
              {translate('policy:guidelineSellContent8')}
            </StyledParagraph>
          </StyledListItem>
          <StyledListItem isCreatorPortal={isCreatorPortal}>
            <StyledParagraph isCreatorPortal={isCreatorPortal}>
              {translate('policy:guidelineSellContent9')}
            </StyledParagraph>
          </StyledListItem>
          {isVerified && (
            <StyledListItem isCreatorPortal={isCreatorPortal}>
              <StyledParagraph isCreatorPortal={isCreatorPortal}>
                {translate('policy:guidelineSellContent10_verifiedCreator')}
              </StyledParagraph>
            </StyledListItem>
          )}
          <StyledListItem isCreatorPortal={isCreatorPortal}>
            <StyledParagraph isCreatorPortal={isCreatorPortal}>
              {translate('policy:guidelineSellContent11')}
            </StyledParagraph>
          </StyledListItem>
          <StyledListItem isCreatorPortal={isCreatorPortal}>
            <StyledParagraph isCreatorPortal={isCreatorPortal}>
              {translate('policy:guidelineSellContent12')}
            </StyledParagraph>
          </StyledListItem>
        </StyledOL>
        <StyledParagraph isCreatorPortal={isCreatorPortal}>
          {translate('policy:guidelineSellFooter')}
        </StyledParagraph>
      </StyledInfoContainer>
    ),
    [isCreatorPortal, isVerified, patterns, translate],
  )

  return (
    <div>
      {handleRenderTitle}
      {handleRenderInfo}
    </div>
  )
}
